import { motion, useReducedMotion } from '@/lib/motion';
import { Bot, MessageSquare, Send, User } from 'lucide-react';
import { useEffect, useState } from 'react';

const CHAT_TURNS = [
  { role: 'visitor' as const, text: 'Do you take walk-ins on weekends?' },
  {
    role: 'agent' as const,
    text: 'We do! Saturdays 9–5 and Sundays 10–4. Walk-ins are first-come, but I can note a request so the team holds a slot.',
    cite: 'Hours & booking policy',
  },
  { role: 'visitor' as const, text: 'Great — hold one for Saturday morning please.' },
  {
    role: 'agent' as const,
    text: 'Done — I’ve captured a Saturday-morning request with your details. The team will confirm shortly.',
  },
];

const LOOP_HOLD_MS = 3400;
const TURN_MS = 1700;

/**
 * Looping website-chat widget mock in the real widget's styling (blue header,
 * rounded window, citation chips). Static (all turns shown) under
 * prefers-reduced-motion. Pass `framed` to wrap it in a faux customer-site
 * browser frame (used on the /chat hero).
 */
export function ChatMock({ framed = false }: { framed?: boolean }): JSX.Element {
  const widget = <ChatWidgetMock />;
  if (!framed) return widget;

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-emerald-500/10">
      {/* Faux customer-site browser chrome */}
      <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        <span className="ml-2 flex-1 truncate rounded-md bg-background px-3 py-1 text-xs text-muted-foreground">
          yourbusiness.com
        </span>
      </div>
      {/* Faux page content with the widget docked bottom-right */}
      <div className="relative min-h-[360px] bg-gradient-to-br from-muted/20 to-background p-6">
        <div className="max-w-xs space-y-3">
          <div className="h-6 w-40 rounded bg-muted" />
          <div className="h-3 w-full rounded bg-muted/70" />
          <div className="h-3 w-5/6 rounded bg-muted/70" />
          <div className="h-3 w-2/3 rounded bg-muted/70" />
          <div className="mt-4 h-9 w-28 rounded-lg bg-[var(--brand-accent)]/30" />
        </div>
        <div className="absolute bottom-5 right-5 w-[300px] max-w-[80%]">{widget}</div>
      </div>
    </div>
  );
}

function ChatWidgetMock(): JSX.Element {
  const reduced = useReducedMotion();
  const total = CHAT_TURNS.length;
  const [visible, setVisible] = useState(reduced ? total : 1);

  useEffect(() => {
    if (reduced) return;
    const timer = setTimeout(
      () => setVisible((v) => (v >= total ? 1 : v + 1)),
      visible >= total ? LOOP_HOLD_MS : TURN_MS,
    );
    return () => clearTimeout(timer);
  }, [reduced, visible, total]);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-xl">
      {/* Widget header — mirrors the real widget's blue bar */}
      <div className="flex items-center gap-2 bg-[#2563eb] px-4 py-3 text-white">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
          <Bot className="h-4 w-4" />
        </span>
        <div className="leading-tight">
          <p className="text-sm font-semibold">Ask us anything</p>
          <p className="flex items-center gap-1 text-[11px] text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
            Online · replies instantly
          </p>
        </div>
      </div>

      {/* Messages — every row stays mounted so the widget's height is constant
          across the loop; reveal is opacity/translate, not node add/remove. A
          growing message list here reflowed the whole centered hero row. */}
      <div className="space-y-3 bg-muted/10 px-3 py-4">
        {CHAT_TURNS.map((turn, i) => {
          const shown = i < visible;
          return (
            <motion.div
              key={i}
              initial={false}
              animate={{ opacity: shown ? 1 : 0, y: shown ? 0 : 8 }}
              transition={{ duration: 0.22 }}
              aria-hidden={!shown}
            >
              <ChatRow turn={turn} typing={!reduced && i === visible - 1 && visible < total} />
            </motion.div>
          );
        })}
      </div>

      {/* Composer (decorative) */}
      <div className="flex items-center gap-2 border-t border-border bg-background px-3 py-2.5">
        <div className="flex-1 rounded-full bg-muted px-3 py-2 text-xs text-muted-foreground">
          Type your message…
        </div>
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2563eb] text-white">
          <Send className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function ChatRow({
  turn,
  typing,
}: {
  turn: (typeof CHAT_TURNS)[number];
  typing?: boolean;
}): JSX.Element {
  const isAgent = turn.role === 'agent';
  return (
    <div className={`flex gap-2 ${isAgent ? 'flex-row' : 'flex-row-reverse'}`}>
      <div
        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
          isAgent ? 'bg-[#2563eb]/15 text-[#2563eb]' : 'bg-muted text-muted-foreground'
        }`}
      >
        {isAgent ? <Bot className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
      </div>
      <div className="max-w-[78%]">
        <div
          className={`rounded-2xl px-3 py-2 text-xs ${
            isAgent
              ? 'rounded-tl-sm bg-background text-foreground shadow-sm'
              : 'rounded-tr-sm bg-[#2563eb] text-white'
          }`}
        >
          {turn.text}
          {typing ? (
            <span className="ml-2 inline-flex gap-1 align-middle">
              <span className="h-1 w-1 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.3s]" />
              <span className="h-1 w-1 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.15s]" />
              <span className="h-1 w-1 animate-bounce rounded-full bg-current opacity-60" />
            </span>
          ) : null}
        </div>
        {isAgent && 'cite' in turn && turn.cite ? (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
            <MessageSquare className="h-2.5 w-2.5" />
            from “{turn.cite}”
          </span>
        ) : null}
      </div>
    </div>
  );
}
